// Agent card ke neeche: "Auto-call is On" patti aur "Add Lead Manually"
// button. Button wahi "Add lead" form (PounceLeadForm) modal me kholta hai;
// lead judte hi Pounce use call karta hai.
//
// `live` = agent chalu hai ya paused. `onAdded` = lead judne ke baad parent
// apne counts / activity taaza kare.
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { addPounceLead } from "../../../Store/apiStore";
import NewModal from "../../NewModal/NewModal";
import PounceLeadForm from "../../FastAgentDetails/PounceOverview/PounceLeadForm";
import LockOverlay from "../../LockOverlay/LockOverlay";
import usePounceAccess, { POUNCE_LOCK_MESSAGE } from "../../../hooks/usePounceAccess";
import styles from "./PounceAddLead.module.css";
import { usePouncePermissions } from "../../PounceLeadsPage/pounceLeadShared";

// `inquiry` = form ka "Notes"; `company` server pe lead ke payload me rehti hai.
const EMPTY_FORM = { name: "", phone: "", email: "", company: "", source: "manual", inquiry: "" };
const SUCCESS_MS = 5000;

const errorText = (err, fallback) =>
  err?.response?.data?.message || err?.response?.data?.error || fallback;

// Server ka error ek hi message hota hai, field ka naam nahi batata. Message ke
// shabd se field pehchaan lete hain taaki error usi field ke neeche dikhe;
// jo kisi field se na mile (network waghairah) wo upar ki patti me rehta hai.
const errorField = (message) => {
  if (/phone|number/i.test(message)) return "phone";
  if (/e-?mail/i.test(message)) return "email";
  if (/name/i.test(message)) return "name";
  return null;
};

const PounceAddLead = ({ live = true, onAdded }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { canEditAgents } = usePouncePermissions();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  // Lead jodna = edit. Support ko sirf tab jab owner ne "edit agents" diya ho.
  const { canEdit } = usePounceAccess();

  useEffect(() => {
    if (!success) return undefined;
    const id = setTimeout(() => setSuccess(""), SUCCESS_MS);
    return () => clearTimeout(id);
  }, [success]);

  const handleAddClick = () => {
    if (!canEdit) return;
    navigate("/add-lead", { state: { returnTo: location.pathname } });
  };

  const close = () => {
    if (saving) return;
    setOpen(false);
    setError("");
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      // consent: form ka required checkbox hi submit tak pahunchne ka rasta hai.
      const res = await addPounceLead({ ...form, consent: true, consent_source: "manual" });
      setSuccess(
        res?.duplicate
          ? "That lead is already being handled."
          : res?.paused
            ? "Lead added. Outbound Caller is off, so it's on hold."
            : "Lead added — calling now."
      );
      setForm(EMPTY_FORM);
      setOpen(false);
      onAdded?.();
    } catch (err) {
      setError(errorText(err, "We couldn't add that lead. Please try again."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className={styles.wrap} aria-label="Add a lead">
      <div className={styles.armed}>
        <span className={styles.armedIcon} aria-hidden="true">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
            <path d="M13 2L4 14h7l-1 8 9-12h-7l1-8Z" fill="#fff" />
          </svg>
        </span>
        <span className={styles.armedText}>
          <span className={`${styles.armedLabel} ${live ? "" : styles.armedLabelOff}`}>
            {live ? "Auto-call is On" : "Auto-call is Off"}
            <span className={styles.armedDot} aria-hidden="true" />
          </span>
          <span className={styles.armedMain}>
            {live ? "Eligible new leads are contacted in ~10 seconds" : "New leads won't be called"}
          </span>
        </span>
      </div>

      <LockOverlay locked={!canEdit} message={POUNCE_LOCK_MESSAGE}>
        <button type="button" className={styles.addBtn} onClick={handleAddClick}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" />
            <path d="M12 8v8M8 12h8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
          Add Lead Manually
        </button>
      </LockOverlay>

      {success && (
        <p className={styles.success} role="status">
          {success}
        </p>
      )}

      {/* Old modal rendering preserved as commented block per spec
      <NewModal
        isOpen={open}
        onClose={close}
        // Subtitle header me hi: "Add Lead" ke neeche, header ki line ke upar.
        title={
          <>
            Add Lead
            <span className={styles.modalSubtitle}>
              Add a new lead and Outbound Caller will contact them automatically.
            </span>
          </>
        }
        height="auto"
        // NewModal ke default (sheet 90vh, body 70vh) yahan fit nahi hote:
        // subtitle se header lamba hai, to header + 70vh body sheet se bahar
        // nikal jaati thi aur neeche ka submit button kat jaata tha. Phone pe
        // `vh` address bar ke peeche ki jagah bhi ginta hai, isliye `dvh`
        // (sirf dikhne wali screen). 130px = sheet ke bahar ki jagah (upar/
        // neeche ka gap + close button), 115px = header.
        maxHeight="calc(100dvh - 130px)"
        bodyMaxHeight="calc(100dvh - 245px)"
      >
        {error && !errorField(error) && (
          <p className={styles.error} role="alert">
            {error}
          </p>
        )}
        <PounceLeadForm
          form={form}
          fieldErrors={error && errorField(error) ? { [errorField(error)]: error } : undefined}
          onFieldChange={(name, value) => {
            setForm((f) => ({ ...f, [name]: value }));
            // Galat field theek karte hi uska error hat jaaye.
            if (errorField(error) === name) setError("");
          }}
          onSubmit={submit}
          saving={saving}
        />
      </NewModal>
      */}
    </section>
  );
};

export default PounceAddLead;
